import { formatDistanceToNow } from 'date-fns';
import { ClipboardList } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/catalyst/badge';
import { useMatter } from '@/hooks/useMatter';
import { useGetMatterQuery } from '@/services/api/mattersService';
import {
  useGetMedicalProvidersQuery,
  useGetTreatmentCheckInsQuery,
} from '@/services/api/treatmentService';
import {
  InferredInjuryType,
  TreatmentCheckInStatus,
  TreatmentProgress,
} from '@/services/types/treatment-types';

import { MedicalExpensesCard } from '../matter/MedicalExpensesCard';
import { TreatmentProvidersSection } from './v3/treatment/TreatmentProvidersSection';
import { TreatmentTimelineCard } from './v3/treatment/TreatmentTimelineCard';

const injuryTypeLabel: Partial<Record<InferredInjuryType, string>> = {
  [InferredInjuryType.SOFT_TISSUE_NECK_BACK]: 'Soft Tissue',
  [InferredInjuryType.SHOULDER_INJURY]: 'Shoulder',
  [InferredInjuryType.KNEE_INJURY]: 'Knee',
  [InferredInjuryType.SPINE_DISC_HERNIATION]: 'Disc Herniation',
  [InferredInjuryType.FRACTURE]: 'Fracture',
  [InferredInjuryType.TBI_HEAD_INJURY]: 'TBI / Head',
  [InferredInjuryType.MULTIPLE_INJURY]: 'Multiple Injuries',
};

export const TreatmentOverviewNew = () => {
  const { matterId } = useMatter();
  const navigate = useNavigate();
  const { data: providers, isLoading } = useGetMedicalProvidersQuery(matterId);
  const { data: checkIns } = useGetTreatmentCheckInsQuery(matterId);
  const { data: matterData } = useGetMatterQuery(matterId);

  // Get the last completed check-in
  const lastCheckIn = useMemo(() => {
    if (!checkIns) return null;
    const completed = checkIns
      .filter(
        (c) => c.status === TreatmentCheckInStatus.COMPLETED && c.check_in_time,
      )
      .sort(
        (a, b) =>
          new Date(b.check_in_time!).getTime() -
          new Date(a.check_in_time!).getTime(),
      );
    return completed[0] ?? null;
  }, [checkIns]);

  // Format time since last check-in
  const timeSinceLastCheckIn = useMemo(() => {
    if (!lastCheckIn?.check_in_time) return null;
    return formatDistanceToNow(new Date(lastCheckIn.check_in_time), {
      addSuffix: false,
    });
  }, [lastCheckIn]);

  // TODO: These would come from a treatment summary API in the future
  // For now, we'll derive what we can from providers or use placeholders
  const treatmentData = useMemo(() => {
    // In the future, this data would come from a dedicated API endpoint
    // For now, return placeholder data that can be replaced
    return {
      primaryInjury: undefined as string | undefined,
      injurySummary: undefined as string | undefined,
      patientCondition: undefined as TreatmentProgress | undefined,
      treatmentStatus: 'ongoing' as const,
      incidentDate: undefined as string | undefined,
    };
  }, []);

  return (
    <div className="flex flex-col gap-4 pr-4 pb-8 bg-white min-h-full max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-zinc-900">Treatment</h1>
            {matterData?.inferred_injury_type &&
              matterData.inferred_injury_type !== InferredInjuryType.UNKNOWN &&
              injuryTypeLabel[matterData.inferred_injury_type] && (
                <Badge color="blue" className="text-[11px]">
                  {injuryTypeLabel[matterData.inferred_injury_type]}
                </Badge>
              )}
          </div>
          {timeSinceLastCheckIn && (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-zinc-100 w-fit">
              <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              <span className="text-[11px] text-zinc-600">
                Last check-in {timeSinceLastCheckIn} ago
              </span>
            </div>
          )}
        </div>

        <Button
          size="sm"
          onClick={() => navigate(`/matters/${matterId}/treatment/check-ins`)}
          className="h-8 text-xs gap-1.5"
        >
          <ClipboardList className="h-3.5 w-3.5" />
          Check-ins
        </Button>
      </div>
      {/* 
      <TreatmentSummaryCard
        providers={providers ?? []}
        isLoading={isLoading}
        primaryInjury={treatmentData.primaryInjury}
        injurySummary={treatmentData.injurySummary}
        patientCondition={treatmentData.patientCondition}
      /> */}

      <TreatmentTimelineCard
        providers={providers ?? []}
        isLoading={isLoading}
        incidentDate={treatmentData.incidentDate}
      />

      {/* Treatment Providers Section */}
      <TreatmentProvidersSection
        providers={providers ?? []}
        isLoading={isLoading}
      />

      {/* Medical Expenses Section */}
      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-zinc-900">
          Medical Expenses
        </h2>
        {matterId && <MedicalExpensesCard matterId={matterId} hideHeader />}
      </div>

    </div>
  );
};

export default TreatmentOverviewNew;
